
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../integrations/supabase/client';
import { Loader2, Trash2 } from 'lucide-react';
import type { Database } from '../../integrations/supabase/types';
import { useQuery } from '@tanstack/react-query';

type Vehicle = Database['public']['Tables']['vehicles']['Row'];

const ManageVehicle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'standard',
    category: 'sedan',
    base_fare: 0,
    price_per_km: 0,
    capacity: 0,
    luggage_capacity: 0,
    image_url: '',
    features: [] as string[],
    is_available: true
  });

  const { data: vehicle } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as Vehicle;
    },
    enabled: !!id
  });

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name,
        description: vehicle.description || '',
        type: vehicle.type,
        category: vehicle.category,
        base_fare: vehicle.base_fare,
        price_per_km: vehicle.price_per_km,
        capacity: vehicle.capacity,
        luggage_capacity: vehicle.luggage_capacity,
        image_url: vehicle.image_url || '',
        features: vehicle.features || [],
        is_available: vehicle.is_available
      });
    }
  }, [vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (id) {
        const { error } = await supabase
          .from('vehicles')
          .update(formData)
          .eq('id', id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('vehicles')
          .insert([formData]);

        if (error) throw error;
      }
      
      navigate('/admin/vehicles');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Failed to save vehicle. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      navigate('/admin/vehicles');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Failed to delete vehicle. Please try again.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{id ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
        {id && (
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-600 rounded-md text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Delete Vehicle
          </button>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Vehicle Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          >
            <option value="standard">Standard</option>
            <option value="luxury">Luxury</option>
            <option value="van">Van</option>
            <option value="suv">SUV</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          >
            <option value="sedan">Sedan</option>
            <option value="suv">SUV</option>
            <option value="van">Van</option>
            <option value="luxury">Luxury</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Base Fare (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.base_fare}
              onChange={(e) => setFormData(prev => ({ ...prev, base_fare: parseFloat(e.target.value) || 0 }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Price per km (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price_per_km}
              onChange={(e) => setFormData(prev => ({ ...prev, price_per_km: parseFloat(e.target.value) || 0 }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Passenger Capacity</label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              required
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Luggage Capacity</label>
            <input
              type="number"
              value={formData.luggage_capacity}
              onChange={(e) => setFormData(prev => ({ ...prev, luggage_capacity: parseInt(e.target.value) || 0 }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              required
              min="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Features (comma-separated)
          </label>
          <input
            type="text"
            value={formData.features.join(', ')}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              features: e.target.value.split(',').map(f => f.trim()).filter(f => f.length > 0)
            }))}
            placeholder="WiFi, Leather seats, Climate control"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          />
          <p className="mt-1 text-sm text-gray-500">Enter features separated by commas</p>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_available"
            checked={formData.is_available}
            onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">
            Vehicle is available for booking
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Vehicle Image URL</label>
          <input
            type="url"
            value={formData.image_url}
            onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
            placeholder="Enter image URL"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            required
          />
          {formData.image_url && (
            <div className="mt-2">
              <img 
                src={formData.image_url} 
                alt="Vehicle preview" 
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = 'https://via.placeholder.com/400x300?text=Invalid+Image+URL';
                }}
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          {isSubmitting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            id ? 'Update Vehicle' : 'Create Vehicle'
          )}
        </button>
      </form>
    </div>
  );
};

export default ManageVehicle;
